import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { FileText, Download, Check, Layout, Code, Sparkles, Briefcase, ArrowLeft, ArrowRight, Loader2, Save, FolderOpen, Trash2, X, FileType, Award, AlertTriangle, History, Eye, ChevronDown, AlignLeft, PanelLeft, Crown, RefreshCw } from "lucide-react";
import axios from "axios";
import ResumeEditor, { structuredToPreview, parseToStructured } from "@/components/ResumeEditor";
import POSLoader from "@/components/POSLoader";
import {
  price as fmtPrice,
  useMarketConfig,
  usePricingForMarket,
} from "@/utils/marketConfig";
import "@/styles/landing.css";

// Error Boundary to catch rendering crashes
class TemplateErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("TemplatePage error:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="ap-root min-h-screen pt-24 flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="w-14 h-14 rounded-2xl grid place-items-center mx-auto mb-4" style={{ background: "#FEF3C7" }}>
              <AlertTriangle className="w-7 h-7" style={{ color: "#d97706" }} />
            </div>
            <h2 className="ap-display text-[20px] font-extrabold text-[var(--ap-ink)] mb-2">Something went wrong</h2>
            <p className="text-[var(--ap-muted)] mb-5">There was an error loading the document templates. Please try again.</p>
            <button
              onClick={() => window.location.href = "/results"}
              className="ap-btn ap-btn-pri ap-btn-md"
            >
              Back to Results
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function titleCaseDocumentTerm(value) {
  const term = String(value || "document").trim() || "document";
  if (term.toLowerCase() === "cv") return "CV";
  return term.charAt(0).toUpperCase() + term.slice(1);
}

function formatMarketDate(value, locale, options) {
  if (!value || !locale) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(locale, options).format(parsed);
  } catch {
    return "";
  }
}

function contentDispositionFilename(headers) {
  const disposition = headers?.["content-disposition"]
    || headers?.get?.("content-disposition")
    || "";
  if (!disposition) return "";

  const encoded = disposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  const quoted = disposition.match(/filename\s*=\s*"([^"]+)"/i);
  const bare = disposition.match(/filename\s*=\s*([^;\s]+)/i);
  let filename = encoded?.[1] || quoted?.[1] || bare?.[1] || "";
  if (encoded?.[1]) {
    try { filename = decodeURIComponent(filename); } catch { /* retain safe raw value */ }
  }
  filename = filename.split(/[\\/]/).pop().replace(/[<>:"|?*\u0000-\u001F]/g, "_").trim();
  return filename;
}

const TEMPLATES = [
  { id: "modern",    name: "Modern",    icon: Sparkles,  desc: "Clean indigo accent — the all-rounder", color: "#2832C2", font: "Helvetica" },
  { id: "classic",   name: "Classic",   icon: Layout,    desc: "Traditional, recruiter-safe serif",     color: "#0A1128", font: "Times" },
  { id: "minimal",   name: "Minimal",   icon: AlignLeft, desc: "Airy, understated, lots of whitespace", color: "#111827", font: "Helvetica" },
  { id: "sidebar",   name: "Sidebar",   icon: PanelLeft, desc: "Two-column with a skills rail",          color: "#0F766E", font: "Helvetica" },
  { id: "tech",      name: "Tech",      icon: Code,      desc: "Monospace headers for engineers",        color: "#4F46E5", font: "Courier" },
  { id: "executive", name: "Executive", icon: Crown,     desc: "Refined serif for senior roles",         color: "#1E3A8A", font: "Times" },
];
const _templateDesign = (id) => {
  const t = TEMPLATES.find((x) => x.id === id) || TEMPLATES[0];
  return { accent: t.color, font: t.font };
};

// If score verification is temporarily unavailable, preserve the user's latest
// editor content as honest single-column text. This is deliberately labelled
// unverified in the UI; a successful /score-structured response remains the
// only source of a verified score and canonical server serialization.
function serializeStructuredForRecovery(structured) {
  const preview = structuredToPreview(structured || {});
  const lines = [preview.name, preview.title, preview.contact]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
  for (const section of preview.sections || []) {
    const title = String(section.title || "").trim();
    const content = (section.content || [])
      .map((value) => String(value || "").trim())
      .filter(Boolean);
    if (!title || !content.length) continue;
    if (lines.length) lines.push("");
    lines.push(title.toUpperCase(), ...content);
  }
  return lines.join("\n").trim();
}

// ── Resume text → {name, contact, sections[]} ───────────────────────────────
function parseResume(text) {
  try {
    if (!text || typeof text !== "string") return { name: "Your Name", contact: "", sections: [] };
    const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
    const name = lines[0] || "Your Name";
    const sectionHeaders = ["contact", "summary", "objective", "profile", "experience",
      "work experience", "education", "skills", "technical skills", "projects",
      "certifications", "awards", "achievements", "languages", "interests"];
    const sections = [];
    let current = { title: "Header", content: [] };
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      const lower = line.toLowerCase().replace(/[^a-z ]/g, "").trim();
      if (line.length < 40 && sectionHeaders.some((h) => lower === h || lower.startsWith(h))) {
        if (current.content.length) sections.push(current);
        current = { title: line.replace(/[:#\-_]/g, "").trim(), content: [] };
      } else {
        current.content.push(line);
      }
    }
    if (current.content.length) sections.push(current);
    return { name, contact: getContact(sections), sections };
  } catch (e) {
    console.error("parseResume error:", e);
    return { name: "Your Name", contact: "", sections: [] };
  }
}

const _CONTACT_RE = /(@|https?:\/\/|linkedin\.com|github\.com|\b\d{10}\b|\+?\d[\d\s().-]{8,})/i;
function getContact(sections) {
  for (const s of sections) for (const l of s.content) {
    if (_CONTACT_RE.test(l) && l.length <= 160) return l;
  }
  return "";
}
const _isBullet = (l) => /^\s*[-•*▪◦·]/.test(l);
const _stripBullet = (l) => l.replace(/^\s*[-•*▪◦·]\s*/, "").trim();
// A non-bullet line that reads like a role/company/date header (so we can bold it).
const _isHeadingLine = (l) => {
  const t = l.trim();
  if (t.length > 90) return false;
  return / \| /.test(t) || /\b(19|20)\d{2}\b/.test(t) || /\b(present|current)\b/i.test(t);
};

// Shared section-body renderer. `skip` = a line to omit (the contact, shown in header).
function SectionBody({ content, skip, accent = "#2832C2", text = "text-gray-700", heading = "text-[#0A1128] font-semibold" }) {
  return content.filter((l) => l !== skip).map((line, j) => {
    if (_isBullet(line)) {
      return (
        <div key={j} className="flex gap-2.5 mb-1.5">
          <span className="mt-[7px] w-1.5 h-1.5 rounded-full shrink-0" style={{ background: accent }} />
          <span className={`text-[13px] leading-relaxed ${text}`}>{_stripBullet(line)}</span>
        </div>
      );
    }
    if (_isHeadingLine(line)) {
      return <p key={j} className={`text-[13.5px] mt-2.5 mb-1 ${heading}`}>{line}</p>;
    }
    return <p key={j} className={`text-[13px] leading-relaxed mb-1 ${text}`}>{line}</p>;
  });
}

// ── Templates (all light / print-clean / ATS-parseable real text) ───────────
// Each accepts an `accent` (hex) + `font` (Helvetica|Times|Courier) so the
// editor's design panel recolours / re-fonts the preview live; the same values
// flow to the PDF download.
const _FF = {
  Helvetica: "'Inter', system-ui, sans-serif",
  Times: "'Georgia', 'Times New Roman', serif",
  Courier: "'JetBrains Mono', monospace",
};
const _ff = (font, fallback) => _FF[font] || fallback;

function ModernTemplate({ data, accent = "#2832C2", font }) {
  const bodySections = data.sections.filter((s) => s.title.toLowerCase() !== "header");
  return (
    <div className="bg-white text-[#0A1128] px-10 py-9 min-h-[700px]" style={{ fontFamily: _ff(font, _FF.Helvetica) }} data-testid="template-modern">
      <header className="mb-6">
        <h1 className="text-[26px] font-extrabold tracking-tight">{data.name}</h1>
        {data.title && <p className="text-[14px] font-semibold mt-0.5" style={{ color: accent }}>{data.title}</p>}
        {data.contact && <p className="text-[12px] text-gray-500 mt-1.5">{data.contact}</p>}
        <div className="h-[3px] w-14 rounded-full mt-3" style={{ background: accent }} />
      </header>
      {bodySections.map((s, i) => (
        <section key={i} className="mb-5">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] mb-2.5" style={{ color: accent }}>{s.title}</h2>
          <SectionBody content={s.content} skip={data.contact} accent={accent} />
        </section>
      ))}
    </div>
  );
}

function ClassicTemplate({ data, accent = "#0A1128", font }) {
  const bodySections = data.sections.filter((s) => s.title.toLowerCase() !== "header");
  return (
    <div className="bg-white text-gray-900 px-10 py-9 min-h-[700px]" style={{ fontFamily: _ff(font, _FF.Times) }} data-testid="template-classic">
      <header className="text-center pb-4 mb-6 border-b-2 border-gray-900">
        <h1 className="text-[24px] font-bold tracking-[0.04em] uppercase">{data.name}</h1>
        {data.title && <p className="text-[13px] font-semibold text-gray-700 mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>{data.title}</p>}
        {data.contact && <p className="text-[12px] text-gray-600 mt-1.5" style={{ fontFamily: "'Inter', sans-serif" }}>{data.contact}</p>}
      </header>
      {bodySections.map((s, i) => (
        <section key={i} className="mb-5">
          <h2 className="text-[12px] font-bold uppercase tracking-[0.16em] border-b border-gray-300 pb-1 mb-2.5" style={{ color: accent }}>{s.title}</h2>
          <SectionBody content={s.content} skip={data.contact} accent={accent} text="text-gray-800" />
        </section>
      ))}
    </div>
  );
}

function MinimalTemplate({ data, accent = "#111827", font }) {
  const bodySections = data.sections.filter((s) => s.title.toLowerCase() !== "header");
  return (
    <div className="bg-white text-gray-900 px-12 py-12 min-h-[700px]" style={{ fontFamily: _ff(font, _FF.Helvetica) }} data-testid="template-minimal">
      <header className="mb-8">
        <h1 className="text-[30px] font-light tracking-tight text-gray-900">{data.name}</h1>
        {data.title && <p className="text-[14px] font-light text-gray-500 mt-1">{data.title}</p>}
        {data.contact && <p className="text-[12px] text-gray-400 mt-2 tracking-wide">{data.contact}</p>}
      </header>
      {bodySections.map((s, i) => (
        <section key={i} className="mb-6 grid grid-cols-[120px_1fr] gap-5">
          <h2 className="text-[10px] font-semibold uppercase tracking-[0.2em] pt-1" style={{ color: accent }}>{s.title}</h2>
          <div className="border-l border-gray-100 pl-5">
            <SectionBody content={s.content} skip={data.contact} accent={accent} text="text-gray-600" />
          </div>
        </section>
      ))}
    </div>
  );
}

function SidebarTemplate({ data, accent = "#0F766E", font }) {
  const railRe = /skill|contact|education|certification|language|award|interest|tool/i;
  const body = data.sections.filter((s) => s.title.toLowerCase() !== "header");
  const rail = body.filter((s) => railRe.test(s.title));
  const main = body.filter((s) => !railRe.test(s.title));
  return (
    <div className="bg-white text-gray-900 min-h-[700px] grid grid-cols-[34%_1fr]" style={{ fontFamily: _ff(font, _FF.Helvetica) }} data-testid="template-sidebar">
      {/* left rail */}
      <aside className="px-6 py-9 border-r" style={{ background: `${accent}10`, borderColor: `${accent}26` }}>
        <h1 className="text-[20px] font-extrabold leading-tight" style={{ color: accent }}>{data.name}</h1>
        {data.title && <p className="text-[12px] font-semibold text-gray-700 mt-1">{data.title}</p>}
        {data.contact && <p className="text-[11px] text-gray-500 mt-2 break-words">{data.contact}</p>}
        {rail.map((s, i) => (
          <section key={i} className="mt-6">
            <h2 className="text-[10px] font-bold uppercase tracking-[0.16em] mb-2" style={{ color: accent }}>{s.title}</h2>
            <SectionBody content={s.content} skip={data.contact} accent={accent} text="text-gray-600" />
          </section>
        ))}
      </aside>
      {/* main column */}
      <main className="px-8 py-9">
        {main.map((s, i) => (
          <section key={i} className="mb-5">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.16em] mb-2.5" style={{ color: accent }}>{s.title}</h2>
            <SectionBody content={s.content} skip={data.contact} accent={accent} />
          </section>
        ))}
      </main>
    </div>
  );
}

function TechTemplate({ data, accent = "#4F46E5", font }) {
  const bodySections = data.sections.filter((s) => s.title.toLowerCase() !== "header");
  return (
    <div className="bg-white text-[#0A1128] px-10 py-9 min-h-[700px]" style={{ fontFamily: _ff(font, _FF.Helvetica) }} data-testid="template-tech">
      <header className="mb-6 pb-4 border-b border-gray-200">
        <h1 className="text-[25px] font-bold tracking-tight">{data.name}</h1>
        {data.title && <p className="text-[13px] font-semibold text-gray-700 mt-0.5">{data.title}</p>}
        <p className="text-[11px] mt-1" style={{ color: accent, fontFamily: "'JetBrains Mono', monospace" }}>{"// "}{data.contact || "engineer"}</p>
      </header>
      {bodySections.map((s, i) => (
        <section key={i} className="mb-5">
          <h2 className="text-[12px] font-semibold mb-2.5" style={{ color: accent, fontFamily: "'JetBrains Mono', monospace" }}>
            <span className="text-gray-300">## </span>{s.title.toLowerCase()}
          </h2>
          <div className="border-l-2 pl-4" style={{ borderColor: `${accent}33` }}>
            <SectionBody content={s.content} skip={data.contact} accent={accent} />
          </div>
        </section>
      ))}
    </div>
  );
}

function ExecutiveTemplate({ data, accent = "#1E3A8A", font }) {
  const bodySections = data.sections.filter((s) => s.title.toLowerCase() !== "header");
  return (
    <div className="bg-white text-gray-900 px-11 py-10 min-h-[700px]" style={{ fontFamily: _ff(font, _FF.Times) }} data-testid="template-executive">
      <header className="mb-7">
        <h1 className="text-[30px] font-bold tracking-wide" style={{ color: accent }}>{data.name}</h1>
        {data.title && <p className="text-[14px] font-semibold text-gray-600 mt-1">{data.title}</p>}
        <div className="w-24 h-[2px] mt-3 mb-2" style={{ background: accent }} />
        {data.contact && <p className="text-[12px] text-gray-500" style={{ fontFamily: "'Inter', sans-serif" }}>{data.contact}</p>}
      </header>
      {bodySections.map((s, i) => (
        <section key={i} className="mb-6">
          <h2 className="text-[12px] font-semibold uppercase tracking-[0.22em] mb-2.5" style={{ color: accent }}>{s.title}</h2>
          <SectionBody content={s.content} skip={data.contact} accent={accent} text="text-gray-700" />
        </section>
      ))}
    </div>
  );
}

const TEMPLATE_COMPONENTS = {
  modern: ModernTemplate, classic: ClassicTemplate, minimal: MinimalTemplate,
  sidebar: SidebarTemplate, tech: TechTemplate, executive: ExecutiveTemplate,
};

function TemplatePageInner() {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  // loader-exit transition phase: "in" while loading, "leaving" for 300ms after
  // load completes (overlay fades out over the rising content), then "done".
  const [loaderPhase, setLoaderPhase] = useState("in");
  useEffect(() => {
    if (loading) { setLoaderPhase("in"); return undefined; }
    setLoaderPhase("leaving");
    const t = setTimeout(() => setLoaderPhase("done"), 320);
    return () => clearTimeout(t);
  }, [loading]);
  const [activeTemplate, setActiveTemplate] = useState("modern");
  const [editMode, setEditMode] = useState(false);
  const [editText, setEditText] = useState("");
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadingFormat, setDownloadingFormat] = useState(null);  // 'pdf' | 'docx' | null
  const [structuredDraft, setStructuredDraft] = useState(null);     // editable resume model
  const [design, setDesign] = useState(_templateDesign("modern"));
  const [liveScore, setLiveScore] = useState(null);
  const [serializedText, setSerializedText] = useState("");         // canonical text from last re-score
  const [scoreStatus, setScoreStatus] = useState("idle");           // idle | scoring | verified | unverified
  const [scoreError, setScoreError] = useState("");
  const [scoreRetryToken, setScoreRetryToken] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [showSavedList, setShowSavedList] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(false);
  // The active text can come from an analysis or a saved editor artifact.
  // Keep its trusted server-side analysis link/market separate from whichever
  // vault card happened to be open before the user loaded it.
  const [artifactAnalysisId, setArtifactAnalysisId] = useState("");
  const [artifactMarketCode, setArtifactMarketCode] = useState("");
  const [vault, setVault] = useState([]);          // past analyses (Resume Vault)
  const [vaultLoading, setVaultLoading] = useState(true);
  const [showArchive, setShowArchive] = useState(false);
  const [edited, setEdited] = useState(false);     // user has actually changed the draft
  // A job-tailored rewrite is a view over the base analysis. Keep it separate
  // so opening or editing a variant never mutates rewritten_resume itself.
  const [jobVariantContext, setJobVariantContext] = useState(null);
  const previewRef = useRef(null);
  const scoreRequestRef = useRef(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedId = searchParams.get("analysis_id");
  const requestedJobVariant = searchParams.get("job_variant");
  // Deep-link straight into the editor (?edit=1) — e.g. the results-page
  // "Add my links in the editor" CTA lands on editing, not preview.
  useEffect(() => {
    if (!loading && analysis && searchParams.get("edit") === "1") setEditMode(true);
  }, [loading, analysis]); // eslint-disable-line react-hooks/exhaustive-deps
  const { getHeaders, user } = useAuth();
  const { cfg: sessionMarket, ready: sessionMarketReady } = useMarketConfig();
  const activeMarketCode = artifactMarketCode
    || (artifactAnalysisId ? analysis?.market : sessionMarket?.market);
  const {
    cfg: analysisMarket,
    ready: analysisMarketReady,
  } = usePricingForMarket(activeMarketCode, sessionMarket, sessionMarketReady);
  const sessionDocTerm = sessionMarketReady
    ? String(sessionMarket.doc_term || "document").toLowerCase()
    : "document";
  const analysisDocTerm = analysisMarketReady
    ? String(analysisMarket.doc_term || "document").toLowerCase()
    : "document";
  const documentTerm = (analysis || artifactMarketCode)
    ? analysisDocTerm
    : sessionDocTerm;
  const documentTitle = titleCaseDocumentTerm(documentTerm);
  const documentPlural = documentTerm === "cv" ? "CVs" : `${documentTerm}s`;
  const documentPluralTitle = documentTerm === "cv" ? "CVs" : `${documentTitle}s`;
  const optimizePrice = analysisMarketReady
    ? fmtPrice(analysisMarket, analysisMarket.prices.one_time)
    : "…";

  useEffect(() => {
    const controller = new AbortController();
    fetchAnalysis(controller.signal);
    return () => controller.abort();
  }, [requestedId, requestedJobVariant]); // eslint-disable-line

  useEffect(() => {
    fetchSavedTemplates();
    fetchVault();
  }, [user?.id]); // eslint-disable-line

  const fetchAnalysis = async (signal) => {
    setLoading(true);
    scoreRequestRef.current += 1;
    setJobVariantContext(null);
    setArtifactAnalysisId("");
    setArtifactMarketCode("");
    setLiveScore(null);
    setScoreStatus("scoring");
    setScoreError("");
    try {
      // A vault card can open a specific analysis (?analysis_id=); otherwise
      // prefer the latest OPTIMIZED one, falling back to the latest analysis so
      // FREE users can edit their uploaded resume too (free edit + live score).
      let data;
      try {
        const url = requestedId ? `${API}/analysis/${requestedId}` : `${API}/analysis/latest-complete`;
        data = (await axios.get(url, { headers: getHeaders(), signal })).data;
      } catch (e) {
        if (!requestedId && e.response?.status === 404) {
          data = (await axios.get(`${API}/analysis/latest`, { headers: getHeaders(), signal })).data;
        } else throw e;
      }
      setAnalysis(data);
      setArtifactAnalysisId(String(data.id || ""));
      setArtifactMarketCode(String(data.market || ""));
      // Seed the editable model: prefer the paid AI-optimized structured object;
      // else parse the optimized text; else the user's uploaded resume (free).
      const baseRewrite = data.rewritten_resume || {};
      const variants = baseRewrite.job_variants;
      const requestedVariant = requestedJobVariant
        && variants
        && typeof variants === "object"
        && Object.prototype.hasOwnProperty.call(variants, requestedJobVariant)
        ? variants[requestedJobVariant]
        : null;
      const variantCandidate = requestedVariant
        && typeof requestedVariant === "object"
        && !Array.isArray(requestedVariant)
        ? requestedVariant
        : null;
      const rawCandidateVariantText = variantCandidate?.improved_resume || variantCandidate?.optimized_resume || "";
      const candidateVariantText = typeof rawCandidateVariantText === "string"
        ? rawCandidateVariantText
        : "";
      const hasCandidateStructured = variantCandidate?.structured
        && typeof variantCandidate.structured === "object"
        && !Array.isArray(variantCandidate.structured)
        && Object.keys(variantCandidate.structured).length;
      const variantRewrite = (candidateVariantText.trim() || hasCandidateStructured)
        ? variantCandidate
        : null;
      const variantText = variantRewrite?.improved_resume || variantRewrite?.optimized_resume || "";
      const rw = variantRewrite
        ? { ...baseRewrite, ...variantRewrite, improved_resume: variantText }
        : baseRewrite;
      setJobVariantContext(variantRewrite ? { key: requestedJobVariant, rewrite: variantRewrite } : null);
      const hasStructured = rw.structured
        && typeof rw.structured === "object"
        && !Array.isArray(rw.structured)
        && Object.keys(rw.structured).length;
      // For a FREE resume (no AI rewrite) the stored text can be a poor,
      // word-per-line PyPDF2 extraction. Prefer the clean layout-aware text the
      // ATS X-ray uses (pdfplumber) so the editor + preview read like a real resume.
      let seedText = rw.improved_resume || (variantRewrite ? "" : data.resume_text) || "";
      if (!variantRewrite && !hasStructured && !rw.improved_resume && data.resume_id) {
        try {
          const p = await axios.get(`${API}/resume/${data.resume_id}/parsed`, { headers: getHeaders(), signal });
          if (p.data?.full_text && p.data.full_text.trim().length > 50) seedText = p.data.full_text;
        } catch (error) {
          if (signal?.aborted || error.code === "ERR_CANCELED") throw error;
          /* fall back to stored text */
        }
      }
      setEditText(seedText);
      const draft = hasStructured ? rw.structured : parseToStructured(seedText);
      setStructuredDraft(draft);
      setSerializedText(rw.improved_resume || seedText || "");
      // A structured-only legacy variant needs one deterministic serialization
      // before it can be saved or downloaded without falling back to base text.
      setEdited(Boolean(variantRewrite && hasStructured && !variantText));
    } catch (err) {
      if (signal?.aborted || err.code === "ERR_CANCELED") return;
      // No unlocked analysis is not a dead end — the Resume Vault below
      // still lists every upload; the editor just renders its locked state.
      setAnalysis(null);
      setScoreStatus("idle");
      if (requestedId && err.response?.status !== 404) {
        toast.error("Couldn't open that analysis.");
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  const baseRewrite = analysis?.rewritten_resume || {};
  const selectedVariantRewrite = jobVariantContext?.rewrite || null;
  const rawSelectedVariantText = selectedVariantRewrite?.improved_resume
    || selectedVariantRewrite?.optimized_resume
    || "";
  const selectedVariantText = typeof rawSelectedVariantText === "string"
    ? rawSelectedVariantText
    : "";
  const activeRewrite = selectedVariantRewrite
    ? {
        ...baseRewrite,
        ...selectedVariantRewrite,
        improved_resume: selectedVariantText,
      }
    : baseRewrite;
  const tailoredJobTitleValue = selectedVariantRewrite?.job_title
    || selectedVariantRewrite?.title
    || selectedVariantRewrite?.job?.title
    || "";
  const tailoredCompanyValue = selectedVariantRewrite?.company
    || selectedVariantRewrite?.company_name
    || selectedVariantRewrite?.job?.company
    || "";
  const tailoredJobTitle = typeof tailoredJobTitleValue === "string" && tailoredJobTitleValue.trim()
    ? tailoredJobTitleValue.trim()
    : "this role";
  const tailoredCompany = typeof tailoredCompanyValue === "string" && tailoredCompanyValue.trim()
    ? tailoredCompanyValue.trim()
    : "this company";

  const fetchVault = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/analyses`, { headers: getHeaders() });
      setVault(res.data.analyses || res.data || []);
    } catch (err) {
      console.error("Failed to fetch vault:", err);
    } finally {
      setVaultLoading(false);
    }
  };

  const fetchSavedTemplates = async () => {
    try {
      const res = await axios.get(`${API}/resume/saved-templates`, { headers: getHeaders() });
      setSavedTemplates(res.data.templates || []);
    } catch (err) {
      console.error("Failed to fetch saved templates:", err);
    }
  };

  const handleSave = async () => {
    // Prefer verified canonical text; after an explicit verification failure,
    // the recovery serializer still preserves the latest edits without
    // claiming that their score was verified.
    if (edited && scoreStatus === "scoring") {
      toast.error(`Please wait for the current ${documentTerm} edits to finish re-scoring.`);
      return;
    }
    if (edited && !serializedText) {
      toast.error("Your latest edits could not be prepared. Retry score verification first.");
      return;
    }
    const text = serializedText || (activeRewrite.improved_resume || "");
    if (!text || text.trim().length < 20) {
      toast.error(`${documentTitle} text is too short to save`);
      return;
    }
    
    setSaving(true);
    try {
      const res = await axios.post(`${API}/resume/save-template`, {
        template_id: activeTemplate,
        resume_text: text,
        analysis_id: artifactAnalysisId,
        name: saveName || undefined
      }, { headers: getHeaders() });
      
      toast.success(res.data.message || `${documentTitle} saved successfully!`);
      setShowSaveModal(false);
      setSaveName("");
      fetchSavedTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to save ${documentTerm}`);
    } finally {
      setSaving(false);
    }
  };

  const handleLoadSaved = async (template) => {
    setLoadingSaved(true);
    try {
      const res = await axios.get(`${API}/resume/saved-template/${template.id}`, { headers: getHeaders() });
      const savedText = String(res.data.resume_text || "");
      const savedAnalysisId = String(res.data.analysis_id || "");
      setArtifactAnalysisId(savedAnalysisId);
      setArtifactMarketCode(String(res.data.market || sessionMarket?.market || ""));
      if (savedAnalysisId && savedAnalysisId !== analysis?.id) {
        try {
          const linked = await axios.get(`${API}/analysis/${savedAnalysisId}`, { headers: getHeaders() });
          setAnalysis(linked.data);
        } catch {
          // The artifact remains loadable; its subsequent server operations
          // will fail closed if the linked analysis is no longer owned.
        }
      }
      scoreRequestRef.current += 1;
      setEditText(savedText);
      setStructuredDraft(parseToStructured(savedText));
      setSerializedText(savedText);
      setLiveScore(null);
      setScoreStatus("scoring");
      setScoreError("");
      setEdited(true);
      setJobVariantContext(null);
      const savedTemplateId = TEMPLATES.some((item) => item.id === res.data.template_id)
        ? res.data.template_id
        : "modern";
      setActiveTemplate(savedTemplateId);
      setDesign(_templateDesign(savedTemplateId));
      setEditMode(true);
      setShowSavedList(false);
      toast.success(`Loaded "${res.data.name}"`);
    } catch (err) {
      toast.error("Failed to load saved template");
    } finally {
      setLoadingSaved(false);
    }
  };

  const handleDeleteSaved = async (templateId, e) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete this saved ${documentTerm}?`)) return;
    
    try {
      await axios.delete(`${API}/resume/saved-template/${templateId}`, { headers: getHeaders() });
      toast.success("Deleted successfully");
      fetchSavedTemplates();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  // Preview renders from the editable structured draft when available (so edits
  // + design show live), else from the optimized text (legacy).
  const resumeData = structuredDraft ? structuredToPreview(structuredDraft)
    : parseResume(String(activeRewrite.improved_resume || ""));
  const TemplateComponent = TEMPLATE_COMPONENTS[activeTemplate];

  // Live re-score: debounced, hits /score-structured (same deterministic engine
  // as a real analysis) so the number tracks edits and equals re-analysis. The
  // response also returns the canonical serialized text used for downloads.
  const caraCtx = (() => {
    const c = {}; for (const r of (analysis?.chatbot_responses || [])) if (r?.question_id) c[r.question_id] = r.answer; return c;
  })();
  useEffect(() => {
    if (!structuredDraft) {
      setScoreStatus("idle");
      return undefined;
    }
    const requestVersion = scoreRequestRef.current;
    const draftForRequest = structuredDraft;
    const editedForRequest = edited;
    setScoreStatus("scoring");
    setScoreError("");
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/resume/score-structured`, {
          structured: draftForRequest,
          target_role: analysis?.target_role || "",
          level: analysis?.level || "",
          target_company: caraCtx.target_company || "",
          career_goal: caraCtx.career_goal || "",
          primary_skills: draftForRequest.technical_skills || [],
          analysis_id: artifactAnalysisId,
        }, { headers: getHeaders() });
        if (requestVersion === scoreRequestRef.current) {
          const canonicalText = String(res.data.text || "");
          if (!canonicalText.trim()) throw new Error("Score response did not include resume text");
          setLiveScore(res.data.score);
          setSerializedText(canonicalText);
          setEditText(canonicalText);
          setScoreStatus("verified");
          setScoreError("");
        }
      } catch {
        if (requestVersion !== scoreRequestRef.current) return;
        if (!editedForRequest) {
          // The stored, unedited resume already has durable text and its stored
          // score. A background verification outage must not replace that copy
          // with a client-side fallback or imply that the user changed it.
          setLiveScore(null);
          setScoreStatus("idle");
          setScoreError("");
          return;
        }
        const recoveryText = serializeStructuredForRecovery(draftForRequest);
        setLiveScore(null);
        setSerializedText(recoveryText);
        setEditText(recoveryText);
        setScoreStatus("unverified");
        setScoreError("Score verification is temporarily unavailable for your latest edits.");
      }
    }, 500);
    return () => clearTimeout(t);
  }, [structuredDraft, edited, scoreRetryToken, artifactAnalysisId]); // eslint-disable-line

  const retryScoreVerification = () => {
    scoreRequestRef.current += 1;
    setLiveScore(null);
    setSerializedText("");
    setScoreStatus("scoring");
    setScoreError("");
    setScoreRetryToken((current) => current + 1);
  };

  // Unified download for both formats. PDF is styled to the selected template;
  // DOCX is the plain ATS-safe Word file. Verified edits use canonical server
  // text; the explicitly unverified recovery path uses a current single-column
  // copy instead of silently exporting stale edits. Successful re-scores still
  // match a later analysis because they use the shared deterministic engine.
  const handleDownload = async (format) => {
    // Prefer the latest prepared edit text (verified canonical or explicitly
    // unverified recovery); only an unedited resume may use stored text.
    if (edited && scoreStatus === "scoring") {
      toast.error(`Please wait for the current ${documentTerm} edits to finish re-scoring.`);
      return;
    }
    if (edited && !serializedText) {
      toast.error("Your latest edits could not be prepared. Retry score verification first.");
      return;
    }
    const text = serializedText || (activeRewrite.improved_resume || "");
    if (!text || text.trim().length < 20) { toast.error("Nothing to download yet."); return; }
    setDownloadingFormat(format);
    const cfg = format === "pdf"
      ? { endpoint: "/resume/render-pdf", body: { template_id: activeTemplate, resume_text: text, accent: design.accent, font: design.font, analysis_id: artifactAnalysisId },
          mime: "application/pdf", fallbackFilename: `${documentTitle}.pdf`, label: "PDF" }
      : { endpoint: "/resume/render-docx", body: { resume_text: text, analysis_id: artifactAnalysisId },
          mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          fallbackFilename: `${documentTitle}.docx`, label: "DOCX" };
    try {
      const res = await axios.post(`${API}${cfg.endpoint}`, cfg.body, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: cfg.mime }));
      const a = document.createElement("a");
      a.href = url;
      // The backend derives the candidate filename and Resume/CV convention.
      // Use its Content-Disposition whenever CORS makes it visible; the neutral
      // market-aware fallback is only for deployments that do not expose it.
      a.download = contentDispositionFilename(res.headers) || cfg.fallbackFilename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`${cfg.label} downloaded — ATS-safe${scoreVerified ? " and score-verified" : ""}.`);
      setShowDownloadModal(false);
    } catch (err) {
      toast.error(`${cfg.label} generation failed.`);
    } finally {
      setDownloadingFormat(null);
    }
  };

  // Calculate score display
  const originalScore = Number(analysis?.score) || 0;
  // Honest number only: the deterministic engine's verified score of the
  // rewritten text — never an invented "+15".
  const improvedScore = Number(
    activeRewrite.estimated_new_score
      ?? activeRewrite.estimated_score
      ?? activeRewrite.score
  ) || originalScore;
  const storedScoreVerified = Boolean(activeRewrite.score_verified);
  // Free users edit their uploaded document; downloads are watermarked until
  // they unlock the analysis-bound AI optimization.
  const isOptimized = analysis?.status === "complete" && Boolean(
    baseRewrite.improved_resume
      || selectedVariantText
      || selectedVariantRewrite?.structured
  );
  // The preview's score must MATCH the report/card until the user actually edits.
  // The live re-score only diverges because it scores a re-parsed/re-serialized
  // copy — so show the canonical analysis score until there's a real edit, then
  // switch to the live number that tracks their changes.
  const previewScore = edited ? liveScore : (isOptimized ? improvedScore : originalScore);
  const scoreVerified = edited
    ? scoreStatus === "verified" && liveScore != null && Boolean(serializedText)
    : storedScoreVerified;
  const scoreUnverified = edited && scoreStatus === "unverified";

  // Smooth loader exit (trialing here before rolling out product-wide): when
  // loading finishes, the content mounts UNDERNEATH and rises in (.ap-rise)
  // while the loader overlay fades out for 300ms, then unmounts — no hard cut,
  // no layout jump. The overlay sits at z-40 so the navbar (z-50) stays
  // visible throughout, and it must be a SIBLING of the content root, never a
  // child: .ap-rise animates transform, and a transformed ancestor would
  // hijack the fixed overlay's containing block (and its opacity fade would
  // multiply onto the overlay). ap-root on the overlay supplies the canvas bg.
  const loaderOverlay = (
    <div className={`ap-root fixed inset-0 z-40 ${loaderPhase === "leaving" ? "ap-loader-leave" : ""}`}>
      <POSLoader fullScreen label={`Opening your ${documentTitle} Vault…`} sub={`Fetching your optimized ${documentTerm} and templates.`} />
    </div>
  );

  if (loading && loaderPhase === "in") {
    return loaderOverlay;
  }

  return (
    <>
      {loaderPhase !== "done" && loaderOverlay}
    <div className="ap-root min-h-screen pt-24 pb-16 ap-rise" data-testid="template-page">
      <div className="ap-section flex flex-col gap-6">
        {/* Header */}
        <div className="ap-mesh rounded-[24px] overflow-hidden relative ap-rise">
          <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-5 p-6 md:p-8">
            <div className="flex items-start gap-3">
              <button onClick={() => { if (window.history.length > 1) navigate(-1); else navigate("/dashboard"); }} className="mt-1 grid place-items-center w-9 h-9 rounded-full bg-white border border-[var(--ap-line)] text-[var(--ap-muted)] hover:text-[var(--ap-ink)] cursor-pointer shrink-0 transition-colors" style={{ boxShadow: "var(--ap-shadow-sm)" }} data-testid="back-to-results">
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div>
                <span className="ap-eyebrow">{documentTitle} Vault</span>
                <h1 className="ap-display ap-h2 mt-2" style={{ fontSize: "clamp(26px,4vw,40px)" }}>
                  Your <span className="ap-grad-text">{documentTerm} library</span>
                </h1>
                <p className="text-[var(--ap-muted)] mt-2 max-w-xl">Every analysis you've run — open an optimized {documentTerm} to edit and download.</p>
              </div>
            </div>
            {/* Score Display — only when an optimized analysis is open (hidden while editing) */}
            {analysis && !editMode && (
              <div className="ap-card shrink-0 flex items-center gap-3 px-5 py-3" style={{ background: "#E7F8EE", borderColor: "rgba(22,163,74,0.25)" }}>
                <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: "rgba(22,163,74,0.15)" }}>
                  <Award className="w-5 h-5" style={{ color: "var(--ap-green)" }} />
                </div>
                <div>
                  <p className="text-xs text-[var(--ap-muted)] m-0">
                    {selectedVariantRewrite ? "Tailored Score" : "Optimized Score"}{scoreVerified && <span className="ml-1" style={{ color: "var(--ap-green)" }} title={`Verified by the same deterministic engine that scored your ${documentTerm}`}>✓ verified</span>}
                  </p>
                  <p className="ap-display text-[22px] font-extrabold m-0" style={{ color: "var(--ap-green)" }}>
                    {previewScore == null ? (scoreUnverified ? "Unverified" : "Re-scoring...") : `${previewScore}/100`}
                    {previewScore != null && previewScore > originalScore && <span className="text-xs ml-1" style={{ color: "rgba(22,163,74,0.7)" }}>(+{previewScore - originalScore})</span>}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Resume Vault: every analysis you've run (hidden while editing) ── */}
        <div className={`ap-card ap-rise ap-rise-1 p-6 ${editMode ? "hidden" : ""}`} data-testid="resume-vault">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg grid place-items-center" style={{ background: "var(--ap-violet-soft)" }}>
              <History className="w-4 h-4" style={{ color: "var(--ap-violet)" }} />
            </div>
            <h2 className="ap-display text-[18px] font-extrabold text-[var(--ap-ink)]">Your analyses</h2>
            {!vaultLoading && <span className="ap-chip" style={{ fontSize: 11, padding: "3px 10px" }}>{vault.length} {vault.length === 1 ? "analysis" : "analyses"}</span>}
          </div>
          {vaultLoading ? (
            <div className="flex items-center gap-2 text-[var(--ap-muted)] text-sm py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading your history…</div>
          ) : vault.length === 0 ? (
            <p className="text-[var(--ap-muted)] text-sm py-2">No analyses yet — upload a {documentTerm} to get started.</p>
          ) : (() => {
            // Best 3 up front. This is the TEMPLATES page, so genuinely
            // OPTIMIZED resumes always outrank score-only checks (a 100-score
            // re-upload test has no template to open); within each group,
            // best achieved score, then recency. Keeps the analysis open in
            // the editor inside the top row instead of buried in the archive.
            const ranked = [...vault].sort((a, b) => {
              const complete = (x) => (x.status === "complete" ? 1 : 0);
              const best = (x) => Math.max(Number(x.improved_score) || 0, Number(x.score) || 0);
              return complete(b) - complete(a) || best(b) - best(a) ||
                     new Date(b.created_at || 0) - new Date(a.created_at || 0);
            });
            const top = ranked.slice(0, 3);
            const archive = ranked.slice(3);
            return (
            <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {top.map((v) => {
                const isOpen = analysis?.id === v.id;
                const complete = v.status === "complete";
                const scoreColor = v.score <= 40 ? "#dc2626" : v.score <= 70 ? "#d97706" : "var(--ap-green)";
                return (
                  <div
                    key={v.id}
                    className={`ap-pop rounded-2xl p-4 transition-all ${isOpen ? "shadow-md" : "hover:border-[var(--ap-violet)]/40"}`}
                    style={{ background: "#fff", border: isOpen ? "2px solid var(--ap-violet)" : "1px solid var(--ap-line)" }}
                    data-testid={`vault-card-${v.id}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 shrink-0" style={{ color: "var(--ap-violet)" }} />
                        <p className="text-[var(--ap-ink)] text-sm font-semibold truncate m-0" title={v.filename}>{v.filename}</p>
                      </div>
                      <span className="ap-chip shrink-0" style={{ fontSize: 10, padding: "3px 9px", background: complete ? "#E7F8EE" : "#F3F4F6", borderColor: "transparent", color: complete ? "var(--ap-green)" : "var(--ap-muted)" }}>
                        {complete ? "Optimized ✓" : "Score only"}
                      </span>
                    </div>
                    <p className="text-[var(--ap-muted)] text-xs truncate mb-3 m-0">
                      {v.target_role || "—"} · {formatMarketDate(
                        v.created_at,
                        sessionMarketReady ? sessionMarket.locale : "",
                        { day: "numeric", month: "short", year: "numeric" },
                      )}
                    </p>
                    <div className="flex items-baseline gap-2 mb-4">
                      <span className="ap-display text-[26px] font-extrabold" style={{ color: scoreColor }}>{v.score}</span>
                      <span className="text-[var(--ap-muted)] text-xs">/100</span>
                      {complete && v.improved_score > v.score && (
                        <span className="text-xs font-semibold" style={{ color: "var(--ap-green)" }}>→ {v.improved_score}/100 optimized</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate(`/results?analysis_id=${v.id}`)}
                        className="ap-btn ap-btn-ghost flex-1"
                        style={{ fontSize: 12.5, padding: "9px 12px" }}
                        data-testid={`vault-report-${v.id}`}
                      >
                        <Eye className="w-3.5 h-3.5" /> View Report
                      </button>
                      {/* Every card opens in the vault (its ATS X-ray + editor) —
                          not just optimized ones — so switching resumes works. */}
                      <button
                        onClick={() => navigate(`/templates?analysis_id=${v.id}`)}
                        disabled={isOpen}
                        className={`ap-btn flex-1 ${isOpen ? "" : "ap-btn-pri"}`}
                        style={{ fontSize: 12.5, padding: "9px 12px", ...(isOpen ? { background: "var(--ap-violet-soft)", color: "var(--ap-violet)", boxShadow: "none", cursor: "default" } : {}) }}
                        data-testid={`vault-open-${v.id}`}
                      >
                        <Layout className="w-3.5 h-3.5" /> {isOpen ? "Open ✓" : (complete ? "Open Template" : "Open")}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Archive: the retries, tucked away */}
            {archive.length > 0 && (
              <div className="mt-4" data-testid="vault-archive">
                <button
                  onClick={() => setShowArchive(s => !s)}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--ap-muted)] hover:text-[var(--ap-violet)] bg-transparent border-0 cursor-pointer py-1.5"
                  data-testid="vault-archive-toggle"
                >
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showArchive ? "rotate-180" : ""}`} />
                  Archive ({archive.length} earlier {archive.length === 1 ? "attempt" : "attempts"})
                </button>
                {showArchive && (
                  <div className="mt-2 rounded-2xl overflow-hidden" style={{ border: "1px solid var(--ap-line)" }}>
                    {archive.map((v, ai) => {
                      const complete = v.status === "complete";
                      const isOpen = analysis?.id === v.id;
                      const sc = v.score <= 40 ? "#dc2626" : v.score <= 70 ? "#d97706" : "var(--ap-green)";
                      return (
                        <div key={v.id} className="flex items-center gap-3 px-4 py-2.5" style={{ borderTop: ai === 0 ? "none" : "1px solid var(--ap-canvas-2)", background: isOpen ? "var(--ap-violet-soft)" : "#fff", borderLeft: isOpen ? "2px solid var(--ap-violet)" : "none" }} data-testid={`archive-row-${v.id}`}>
                          {isOpen && <span className="text-[10px] font-bold uppercase shrink-0" style={{ color: "var(--ap-violet)" }}>Open</span>}
                          <FileText className="w-3.5 h-3.5 shrink-0" style={{ color: "#9ca3af" }} />
                          <span className="text-[var(--ap-ink)] text-xs font-medium truncate flex-1 min-w-0" title={v.filename}>{v.filename}</span>
                          <span className="text-[var(--ap-muted)] text-[11px] hidden sm:block shrink-0">
                            {formatMarketDate(
                              v.created_at,
                              sessionMarketReady ? sessionMarket.locale : "",
                              { day: "numeric", month: "short" },
                            )}
                          </span>
                          <span className="ap-display text-xs font-bold shrink-0" style={{ color: sc }}>
                            {v.score}{complete && v.improved_score > v.score ? <span style={{ color: "var(--ap-green)" }}> → {v.improved_score}</span> : ""}
                          </span>
                          <button onClick={() => navigate(`/results?analysis_id=${v.id}`)} title="View report"
                            className="p-1.5 rounded-md text-[var(--ap-muted)] hover:text-[var(--ap-violet)] bg-transparent border-0 cursor-pointer shrink-0 transition-colors">
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => navigate(`/templates?analysis_id=${v.id}`)} title="Open in vault"
                            className="p-1.5 rounded-md text-[var(--ap-muted)] hover:text-[var(--ap-violet)] bg-transparent border-0 cursor-pointer shrink-0 transition-colors">
                            <Layout className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            </>
            );
          })()}
        </div>

        {!analysis ? (
          /* Locked editor — vault above still works; templates need an
             optimized (unlocked) analysis to have something to edit. */
          <div className="ap-card rounded-2xl px-6 py-12 text-center" style={{ border: "2px dashed var(--ap-line)", boxShadow: "none" }} data-testid="templates-locked">
            <div className="w-14 h-14 rounded-2xl grid place-items-center mx-auto mb-4" style={{ background: "var(--ap-violet-soft)" }}>
              <Layout className="w-7 h-7" style={{ color: "var(--ap-violet)" }} />
            </div>
            <h3 className="ap-display text-[20px] font-extrabold text-[var(--ap-ink)] mb-2">No optimized {documentTerm} to edit yet</h3>
            <p className="text-[var(--ap-muted)] text-sm max-w-md mx-auto mb-6">
              Templates work on your <span className="font-semibold text-[var(--ap-ink)]">optimized</span> {documentTerm}.
              {vault.length > 0
                ? ` Open a report above and unlock the fixes — your rewritten, engine-verified ${documentTerm} becomes editable and downloadable here.`
                : " Get your free score first — then unlock the fixes to edit and download it here."}
            </p>
            <button
              onClick={() => {
                if (vault.length > 0) {
                  const latest = [...vault].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0];
                  navigate(`/results?analysis_id=${latest.id}`);
                } else {
                  navigate("/intake");
                }
              }}
              className="ap-btn ap-btn-pri ap-btn-md"
              data-testid="templates-locked-cta"
            >
              {vault.length > 0 ? "Open my latest report" : "Get my free score"}
            </button>
          </div>
        ) : (
        <>
        {selectedVariantRewrite && (
          <div
            className="ap-card ap-rise flex items-center gap-3 px-5 py-4"
            style={{ borderColor: "rgba(91,61,245,0.3)", background: "var(--ap-violet-soft)", boxShadow: "none" }}
            data-testid="job-variant-banner"
          >
            <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: "#fff" }}>
              <Briefcase className="w-5 h-5" style={{ color: "var(--ap-violet)" }} />
            </div>
            <div className="min-w-0">
              <p className="ap-display text-sm font-extrabold text-[var(--ap-ink)] m-0">
                Tailored for {tailoredJobTitle} at {tailoredCompany}
              </p>
              <p className="text-xs text-[var(--ap-muted)] mt-1 mb-0">
                This job-specific copy is open. Your base optimized {documentTerm} remains unchanged.
              </p>
            </div>
          </div>
        )}
        {scoreUnverified && (
          <div
            className="ap-card ap-rise flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4"
            style={{ borderColor: "rgba(217,119,6,0.35)", background: "#FFFBEB", boxShadow: "none" }}
            role="alert"
            data-testid="resume-score-recovery"
          >
            <AlertTriangle className="w-5 h-5 shrink-0" style={{ color: "#d97706" }} />
            <p className="text-sm text-[var(--ap-ink-2)] m-0 flex-1">
              {scoreError} Save and download will use the current editor content, but PlacementOS will not label its score as verified.
            </p>
            <button type="button" onClick={retryScoreVerification} className="ap-btn ap-btn-ghost shrink-0">
              <RefreshCw className="w-4 h-4" /> Retry verification
            </button>
          </div>
        )}
        {/* The full ATS X-ray lives on the Report (the first/diagnosis screen). Link
            back so the vault stays focused on rebuild + download — no duplication. */}
        <button
          onClick={() => navigate(`/results?analysis_id=${analysis.id}`)}
          className="ap-card ap-rise w-full flex items-center gap-3 px-5 py-3.5 text-left cursor-pointer transition-colors hover:border-[var(--ap-violet)]"
          data-testid="vault-xray-link"
        >
          <div className="w-9 h-9 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--ap-violet-soft)" }}>
            <FileText className="w-4 h-4" style={{ color: "var(--ap-violet)" }} />
          </div>
          <span className="text-sm text-[var(--ap-ink-2)] flex-1">See this {documentTerm} the way an ATS reads it — the sections it caught and the gaps to fix.</span>
          <span className="text-sm font-semibold inline-flex items-center gap-1 shrink-0" style={{ color: "var(--ap-violet)" }}>View report <ArrowRight className="w-4 h-4" /></span>
        </button>
        {/* Free users edit their upload; the upgrade price follows this analysis. */}
        {!isOptimized && (
          <div className="ap-card rounded-2xl px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" style={{ borderColor: "rgba(91,61,245,0.3)", background: "linear-gradient(108deg, rgba(91,61,245,0.08), rgba(199,92,142,0.06))", boxShadow: "none" }} data-testid="ai-optimize-cta">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--ap-violet-soft)" }}>
                <Sparkles className="w-5 h-5" style={{ color: "var(--ap-violet)" }} />
              </div>
              <div>
                <p className="text-[var(--ap-ink)] text-sm font-bold m-0">Editing your uploaded {documentTerm} — free.</p>
                <p className="text-[var(--ap-muted)] text-xs mt-0.5 m-0">Let our AI strengthen keywords and bullet clarity, then remove the download watermark.</p>
              </div>
            </div>
            <button
              onClick={() => navigate(`/results?analysis_id=${analysis.id}`)}
              className="ap-btn ap-btn-fire ap-btn-md shrink-0"
              data-testid="ai-optimize-btn"
            >
              <Sparkles className="w-4 h-4" /> AI-optimize — {optimizePrice}
            </button>
          </div>
        )}
        <div className={`grid grid-cols-1 gap-6 ${editMode ? "" : "lg:grid-cols-[300px_1fr]"}`}>
          {/* Sidebar — hidden while editing so the editor + live preview claim the full width */}
          {!editMode && (
          <div className="ap-card ap-rise ap-rise-1 p-5 self-start space-y-4">
            <p className="ap-eyebrow">Select Template</p>
            <div className="space-y-3">
              {TEMPLATES.map(t => (
                <button
                  key={t.id}
                  onClick={() => { setActiveTemplate(t.id); setDesign(_templateDesign(t.id)); }}
                  className="ap-pop w-full text-left p-3.5 rounded-2xl transition-all cursor-pointer"
                  style={{ border: activeTemplate === t.id ? "2px solid var(--ap-violet)" : "1px solid var(--ap-line)", background: activeTemplate === t.id ? "var(--ap-violet-soft)" : "#fff" }}
                  data-testid={`template-select-${t.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ backgroundColor: `${t.color}15` }}>
                      <t.icon className="w-5 h-5" style={{ color: t.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[var(--ap-ink)] text-sm font-semibold">{t.name}</div>
                      <div className="text-[var(--ap-muted)] text-xs">{t.desc}</div>
                    </div>
                    {activeTemplate === t.id && <Check className="w-5 h-5 shrink-0" style={{ color: "var(--ap-violet)" }} />}
                  </div>
                </button>
              ))}
            </div>

            <div className="ap-divider mt-5" />
            <div className="space-y-3 pt-1">
              {/* Download — opens a format-choice modal (PDF styled / DOCX ATS-safe) */}
              <button onClick={() => setShowDownloadModal(true)} className="ap-btn ap-btn-pri w-full" style={{ fontSize: 14, padding: "13px 18px", borderRadius: 14 }} data-testid="download-btn">
                <Download className="w-4 h-4" />
                <span className="flex-1 text-left">Download {documentTerm}</span>
                <span className="ap-chip" style={{ fontSize: 10, padding: "2px 8px", background: "rgba(255,255,255,0.2)", borderColor: "transparent", color: "#fff", boxShadow: "none" }}>PDF · DOCX</span>
              </button>

              {/* Save Button */}
              <button
                onClick={() => setShowSaveModal(true)}
                className="ap-btn w-full"
                style={{ fontSize: 14, padding: "12px 18px", borderRadius: 14, background: "var(--ap-green)", color: "#fff" }}
                data-testid="save-template-btn"
              >
                <Save className="w-4 h-4" /> Save {documentTitle}
              </button>

              {/* Load Saved Button */}
              {savedTemplates.length > 0 && (
                <button
                  onClick={() => setShowSavedList(true)}
                  className="ap-btn ap-btn-ghost w-full"
                  style={{ fontSize: 14, padding: "12px 18px", borderRadius: 14, color: "var(--ap-green)", borderColor: "var(--ap-green)" }}
                  data-testid="load-saved-btn"
                >
                  <FolderOpen className="w-4 h-4" /> My Saved ({savedTemplates.length})
                </button>
              )}

              <button
                onClick={() => setEditMode(!editMode)}
                className="ap-btn ap-btn-ghost w-full"
                style={{ fontSize: 14, padding: "12px 18px", borderRadius: 14 }}
                data-testid="toggle-edit-btn"
              >
                <FileText className="w-4 h-4" /> {editMode ? "Preview Mode" : `Edit ${documentTitle}`}
              </button>
            </div>
          </div>
          )}

          {/* Preview */}
          <div>
            <Card className="ap-card overflow-hidden" style={{ borderRadius: 22 }}>
              <CardHeader className="py-3 px-4 flex flex-row items-center justify-between gap-3" style={{ borderBottom: "1px solid var(--ap-line)" }}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="ap-chip shrink-0" style={{ fontSize: 11, padding: "3px 10px", background: "#E7F8EE", borderColor: "transparent", color: "var(--ap-green)" }}>{editMode ? "EDITING" : "LIVE PREVIEW"}</span>
                  <span className="text-[var(--ap-muted)] text-xs truncate">{TEMPLATES.find(t => t.id === activeTemplate)?.name} Template</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {previewScore != null && previewScore > 0 && (
                    <span className="ap-display inline-flex items-center gap-1 text-xs font-bold" style={{ color: "var(--ap-green)" }} title={edited ? "Live score of your current edits" : `Your ${documentTitle}'s ATS score — matches your report`}>
                      <Award className="w-3.5 h-3.5" /> {previewScore}/100
                    </span>
                  )}
                  {/* In edit mode the sidebar is hidden, so surface its actions here */}
                  {editMode && (
                    <>
                      <button type="button" onClick={() => setShowSaveModal(true)} title={`Save ${documentTerm}`}
                        className="ap-btn ap-btn-ghost" style={{ fontSize: 12, padding: "7px 12px", color: "var(--ap-green)", borderColor: "rgba(22,163,74,0.4)" }} data-testid="edit-save-btn">
                        <Save className="w-3.5 h-3.5" /> Save
                      </button>
                      <button type="button" onClick={() => setShowDownloadModal(true)} title={`Download ${documentTerm}`}
                        className="ap-btn ap-btn-pri" style={{ fontSize: 12, padding: "7px 12px" }} data-testid="edit-download-btn">
                        <Download className="w-3.5 h-3.5" /> Download
                      </button>
                      <button type="button" onClick={() => setEditMode(false)} title="Back to full preview"
                        className="ap-btn ap-btn-ghost" style={{ fontSize: 12, padding: "7px 12px" }} data-testid="edit-preview-btn">
                        <FileText className="w-3.5 h-3.5" /> Preview
                      </button>
                    </>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {editMode ? (
                  <div className="grid grid-cols-1 lg:grid-cols-[minmax(380px,460px)_1fr] divide-x divide-[var(--ap-line)]">
                    <div className="p-5 max-h-[82vh] overflow-auto bg-[#fafbff]">
                      {structuredDraft && (
                        <ResumeEditor value={structuredDraft} design={design}
                          onChange={(v) => {
                            scoreRequestRef.current += 1;
                            setStructuredDraft(v);
                            setLiveScore(null);
                            setSerializedText("");
                            setScoreStatus("scoring");
                            setScoreError("");
                            setEdited(true);
                          }} onDesignChange={setDesign} />
                      )}
                    </div>
                    <div className="overflow-auto max-h-[82vh] hidden lg:block bg-[#f1f3f9]">
                      {TemplateComponent && <TemplateComponent data={resumeData} accent={design.accent} font={design.font} />}
                    </div>
                  </div>
                ) : (
                  <div ref={previewRef} className="overflow-auto max-h-[75vh]">
                    {TemplateComponent && <TemplateComponent data={resumeData} accent={design.accent} font={design.font} />}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        </>
        )}
      </div>

      {/* Download format-choice modal */}
      {showDownloadModal && (
        <div className="ap-root fixed inset-0 flex items-center justify-center p-4" style={{ background: "rgba(12,17,29,0.6)", backdropFilter: "blur(4px)", zIndex: 50 }} onClick={() => !downloadingFormat && setShowDownloadModal(false)} data-testid="download-modal">
          <div className="ap-card ap-rise p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="ap-display text-[18px] font-extrabold text-[var(--ap-ink)] m-0">Download your {documentTerm}</h3>
              <button onClick={() => !downloadingFormat && setShowDownloadModal(false)} className="text-[var(--ap-muted)] hover:text-[var(--ap-ink)] bg-transparent border-0 cursor-pointer" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-[var(--ap-muted)] text-sm mb-3">
              Both formats use the same single-column ATS-safe content.
              {scoreVerified && (
                <span className="inline-flex items-center gap-1 ml-1 font-semibold" style={{ color: "var(--ap-green)" }}>
                  <Award className="w-3.5 h-3.5" /> {previewScore}/100 verified
                </span>
              )}
            </p>
            {scoreUnverified && (
              <div
                className="mb-4 flex items-start gap-2 rounded-xl px-3 py-2 text-xs"
                style={{ color: "#92400E", background: "#FFFBEB", border: "1px solid rgba(217,119,6,0.35)" }}
                role="status"
                data-testid="download-unverified-notice"
              >
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>Your latest edits are included, but their score is not verified. You can export this copy now or close this dialog and retry verification.</span>
              </div>
            )}
            {!isOptimized && (
              <div className="mb-4 text-xs rounded-xl px-3 py-2" style={{ color: "var(--ap-violet)", background: "var(--ap-violet-soft)", border: "1px solid rgba(91,61,245,0.2)" }}>
                Free downloads carry a small PlacementOS footer. <button onClick={() => { setShowDownloadModal(false); navigate(`/results?analysis_id=${analysis.id}`); }} className="font-semibold underline bg-transparent border-0 cursor-pointer p-0" style={{ color: "var(--ap-violet)" }}>Unlock the clean copy ({optimizePrice})</button> — also includes AI optimization.
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* PDF — default / styled */}
              <button
                onClick={() => handleDownload("pdf")}
                disabled={!!downloadingFormat}
                className="ap-pop text-left p-4 rounded-2xl transition-all cursor-pointer disabled:opacity-60 relative"
                style={{ border: "2px solid var(--ap-violet)", background: "var(--ap-violet-soft)" }}
                data-testid="download-pdf-card"
              >
                <span className="ap-chip absolute top-3 right-3" style={{ fontSize: 9, padding: "2px 8px", background: "var(--ap-violet)", borderColor: "transparent", color: "#fff", boxShadow: "none" }}>DEFAULT</span>
                <FileText className="w-6 h-6 mb-2" style={{ color: "var(--ap-violet)" }} />
                <p className="font-bold text-[var(--ap-ink)] text-sm m-0">PDF</p>
                <p className="text-[var(--ap-muted)] text-xs mt-1 m-0">Styled to the <span className="font-medium">{TEMPLATES.find(t => t.id === activeTemplate)?.name}</span> template — looks like the preview.</p>
                {["sidebar", "tech"].includes(activeTemplate) && (
                  <p className="text-[11px] mt-2 m-0" style={{ color: "var(--ap-violet)" }}>Exports as a single-column, ATS-safe layout.</p>
                )}
                <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: "var(--ap-violet)" }}>
                  {downloadingFormat === "pdf" ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating…</>) : (<><Download className="w-3.5 h-3.5" /> Download PDF</>)}
                </div>
              </button>
              {/* DOCX — ATS-safe */}
              <button
                onClick={() => handleDownload("docx")}
                disabled={!!downloadingFormat}
                className="ap-pop text-left p-4 rounded-2xl transition-all cursor-pointer disabled:opacity-60 relative"
                style={{ border: "1px solid var(--ap-line)", background: "#fff" }}
                data-testid="download-docx-card"
              >
                <span className="ap-chip absolute top-3 right-3" style={{ fontSize: 9, padding: "2px 8px", background: "#E7F8EE", borderColor: "transparent", color: "var(--ap-green)", boxShadow: "none" }}>ATS-SAFE</span>
                <FileType className="w-6 h-6 mb-2" style={{ color: "var(--ap-green)" }} />
                <p className="font-bold text-[var(--ap-ink)] text-sm m-0">DOCX</p>
                <p className="text-[var(--ap-muted)] text-xs mt-1 m-0">Plain Word file every ATS reads cleanly. Best for portal uploads.</p>
                <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: "var(--ap-green)" }}>
                  {downloadingFormat === "docx" ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating…</>) : (<><Download className="w-3.5 h-3.5" /> Download DOCX</>)}
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Modal */}
      {showSaveModal && (
        <div className="ap-root fixed inset-0 flex items-center justify-center p-4" style={{ background: "rgba(12,17,29,0.55)", backdropFilter: "blur(2px)", zIndex: 50 }}>
          <div className="ap-card ap-rise p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="ap-display text-[18px] font-extrabold text-[var(--ap-ink)] m-0">Save {documentTitle}</h3>
              <button
                onClick={() => setShowSaveModal(false)}
                className="text-[var(--ap-muted)] hover:text-[var(--ap-ink)] bg-transparent border-0 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-[var(--ap-muted)] text-sm mb-4">
              Save your edited {documentTerm} to access it later. It will be saved with the "{TEMPLATES.find(t => t.id === activeTemplate)?.name}" template.
            </p>
            {scoreUnverified && (
              <div
                className="mb-4 flex items-start gap-2 rounded-xl px-3 py-2 text-xs"
                style={{ color: "#92400E", background: "#FFFBEB", border: "1px solid rgba(217,119,6,0.35)" }}
                role="status"
                data-testid="save-unverified-notice"
              >
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>Your current edits will be saved, but their score is not verified.</span>
              </div>
            )}
            <input
              type="text"
              placeholder={`${documentTitle} name (optional)`}
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              className="w-full text-sm mb-4"
              style={{ padding: "0.75rem 1rem", borderRadius: "0.75rem", border: "1px solid var(--ap-line)", outline: "none", boxSizing: "border-box", color: "var(--ap-ink)" }}
              onFocus={(e) => e.target.style.borderColor = "var(--ap-violet)"}
              onBlur={(e) => e.target.style.borderColor = "var(--ap-line)"}
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowSaveModal(false)}
                className="ap-btn ap-btn-ghost ap-btn-md flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="ap-btn ap-btn-md flex-1 disabled:opacity-50"
                style={{ background: "var(--ap-green)", color: "#fff" }}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Saved Templates List Modal */}
      {showSavedList && (
        <div className="ap-root fixed inset-0 flex items-center justify-center p-4" style={{ background: "rgba(12,17,29,0.55)", backdropFilter: "blur(2px)", zIndex: 50 }}>
          <div className="ap-card ap-rise p-6 max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col relative">
            <div className="flex items-center justify-between mb-4">
              <h3 className="ap-display text-[18px] font-extrabold text-[var(--ap-ink)] m-0">My Saved {documentPluralTitle}</h3>
              <button
                onClick={() => setShowSavedList(false)}
                className="text-[var(--ap-muted)] hover:text-[var(--ap-ink)] bg-transparent border-0 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 space-y-2">
              {savedTemplates.map((template) => (
                <div
                  key={template.id}
                  onClick={() => handleLoadSaved(template)}
                  className="ap-pop p-4 rounded-2xl cursor-pointer transition-all flex items-center justify-between group"
                  style={{ border: "1px solid var(--ap-line)", background: "#fff" }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[var(--ap-ink)] text-sm truncate">{template.name}</div>
                    <div className="text-xs text-[var(--ap-muted)] flex items-center gap-2 mt-1">
                      <span className="ap-chip capitalize" style={{ fontSize: 11, padding: "2px 9px", background: "var(--ap-canvas)", color: "var(--ap-muted)" }}>{template.template_id}</span>
                      <span>{new Date(template.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleDeleteSaved(template.id, e)}
                    className="text-[var(--ap-muted)] hover:text-red-500 bg-transparent border-0 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {savedTemplates.length === 0 && (
                <p className="text-center text-[var(--ap-muted)] py-8">No saved {documentPlural} yet</p>
              )}
            </div>
            {loadingSaved && (
              <div className="absolute inset-0 flex items-center justify-center rounded-[22px]" style={{ background: "rgba(255,255,255,0.8)" }}>
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--ap-violet)" }} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    </>
  );
}

export default function TemplatePage() {
  return (
    <TemplateErrorBoundary>
      <TemplatePageInner />
    </TemplateErrorBoundary>
  );
}

